import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '@/App';
import axios from 'axios';
import { X, UserPlus, Check, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NotificationPanel = ({ onClose, onRequestsChange }) => {
  const { auth } = useContext(AuthContext);
  const [notifications, setNotifications] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadNotifications();
    loadFriendRequests();
  }, []);

  const loadNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to load notifications');
    }
  };

  const loadFriendRequests = async () => {
    try {
      const response = await axios.get(`${API}/friends/requests`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      setFriendRequests(response.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const handleAcceptRequest = async (requestId) => {
    try {
      await axios.put(
        `${API}/friends/${requestId}/accept`,
        {},
        { headers: { Authorization: `Bearer ${auth.token}` } }
      );
      toast.success('Friend request accepted!');
      loadFriendRequests();
      if (onRequestsChange) onRequestsChange();
    } catch (error) {
      toast.error('Failed to accept request');
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      await axios.put(
        `${API}/friends/${requestId}/reject`,
        {},
        { headers: { Authorization: `Bearer ${auth.token}` } }
      );
      toast.success('Friend request rejected');
      loadFriendRequests();
      if (onRequestsChange) onRequestsChange();
    } catch (error) {
      toast.error('Failed to reject request');
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(
        `${API}/notifications/${notificationId}/read`,
        {},
        { headers: { Authorization: `Bearer ${auth.token}` } }
      );
      loadNotifications();
    } catch (error) {
      console.error('Failed to mark as read');
    }
  };

  const totalCount = friendRequests.length + notifications.filter(n => !n.read_at).length;

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose}></div>
      <div className="absolute right-0 top-12 w-96 bg-[#0D1221] border border-white/20 rounded-xl shadow-2xl z-50" data-testid="notification-panel">
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <h3 className="heading-font font-bold text-lg text-white">Notifications</h3>
          <Button variant="ghost" size="icon" onClick={onClose} className="rounded-lg text-gray-400 hover:text-white hover:bg-white/10">
            <X size={18} />
          </Button>
        </div>

        <div className="max-h-96 overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-400 mx-auto"></div>
            </div>
          ) : (
            <>
              {/* Friend Requests Section */}
              {friendRequests.length > 0 && (
                <div className="border-b border-white/10">
                  <div className="px-4 py-2 bg-teal-500/10">
                    <p className="text-xs font-semibold text-teal-400 uppercase tracking-wide flex items-center gap-2">
                      <UserPlus size={14} />
                      Friend Requests ({friendRequests.length})
                    </p>
                  </div>
                  <div className="divide-y divide-white/5">
                    {friendRequests.map((req) => (
                      <div
                        key={req.request_id}
                        className="p-4 hover:bg-white/5 transition-all"
                        data-testid={`notif-friend-request-${req.request_id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-semibold text-white text-sm">{req.from_user.name}</p>
                            <p className="text-xs text-gray-400">{req.from_user.email}</p>
                            <p className="text-xs text-gray-500 mt-1">wants to be your friend</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              className="h-8 px-3 bg-teal-500 hover:bg-teal-600 text-white"
                              onClick={() => handleAcceptRequest(req.request_id)}
                              data-testid={`accept-notif-${req.request_id}`}
                            >
                              <Check size={14} />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 px-3 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                              onClick={() => handleRejectRequest(req.request_id)}
                              data-testid={`reject-notif-${req.request_id}`}
                            >
                              <XCircle size={14} />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Regular Notifications Section */}
              {notifications.length > 0 ? (
                <div className="divide-y divide-white/5">
                  {notifications.map((notif) => (
                    <div
                      key={notif.id}
                      className={`p-4 hover:bg-white/5 cursor-pointer transition-all ${
                        !notif.read_at ? 'bg-teal-500/5 border-l-2 border-teal-400' : ''
                      }`}
                      onClick={() => !notif.read_at && markAsRead(notif.id)}
                      data-testid={`notification-${notif.id}`}
                    >
                      <p className="text-sm text-white">{notif.message}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(notif.sent_at).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              ) : friendRequests.length === 0 ? (
                <div className="p-8 text-center text-gray-500 body-font" data-testid="no-notifications">
                  No notifications yet
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default NotificationPanel;